import { Fragment, useId, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import type { CurrencyTotal } from '../../platform/money/totals'
import { Icon } from '../../platform/icons/Icon'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { MerchantAvatar, type MerchantIdentity } from '../../platform/primitives/MerchantAvatar'
import { Money } from '../../platform/primitives/Money'
import { recurringDetailPath } from './recurringPaths'
import { SECTION_ORDER, type Section, type SectionKey } from './listSections'
import { useAmountChangeShown } from './recurringAnalytics'
import { formatDue, relativeDue, type LocalDate } from './recurringRules'
import { frequencyLabel, type Entry } from './recurringWire'
import type { AmountChanges } from './useAmountChanges'
import { entryAmountCents } from './useRecurring'

export type AccountNames = ReadonlyMap<string, string>

const TITLES: Record<SectionKey, (month: string) => string> = {
  overdue: () => 'Overdue',
  upcoming: () => 'Upcoming',
  skipped: () => 'Skipped',
  paid: (month) => `Paid in ${month}`,
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)

/**
 * The row's second line (R3, R5). An account the lookup cannot name — the
 * accounts query failed or has not answered — is left out rather than shown as
 * a gap: a missing mask never errors the row.
 */
export function sublineOf(e: Entry, key: SectionKey, today: LocalDate, accounts: AccountNames): string {
  const account = e.bill.accountId ? accounts.get(e.bill.accountId) : undefined
  const freq = lowerFirst(frequencyLabel(e.bill.frequency))
  const parts: (string | undefined)[] =
    key === 'paid'
      ? [`Paid ${formatDue(e.payment?.paidOn ?? e.dueDate, 'short')}`]
      : key === 'overdue'
        ? [`Due ${formatDue(e.dueDate, 'short')}`, relativeDue(e.dueDate, today).text, account]
        : key === 'upcoming'
          ? [formatDue(e.dueDate, 'short'), lowerFirst(relativeDue(e.dueDate, today).text), freq, account]
          : [formatDue(e.dueDate, 'short'), freq, account]
  return parts.filter((p): p is string => p !== undefined).join(' · ')
}

function identityOf(e: Entry): MerchantIdentity {
  return {
    id: e.bill.id,
    name: e.bill.merchant || e.bill.name,
    type: 'other',
    icon: e.bill.icon,
    icon_url: e.bill.iconUrl,
    is_global: false,
  }
}

/** The row's marker chips. Shared with the Passbook, so the two cannot drift. */
export function Chips({ e, changes }: { e: Entry; changes: AmountChanges }) {
  const change = changes[e.bill.id]
  // Counted where the chip is drawn, once per bill per screen mount (the screen's scope).
  useAmountChangeShown(e.bill.id, change)
  const reconnect = e.bill.accountStatus === 'reauth_required' || e.bill.accountStatus === 'disconnected'
  const pills: ReactNode[] = []
  // Neutral pill: --accent-soft is the selection system's tint (rule 9), not a marker's.
  if (e.bill.isInflow) pills.push(<span key="income" className="rec-pill">Income</span>)
  // Why a row sits under Skipped (`sectionOf`): skipped, paused, or an inactive bill.
  if (e.status === 'skipped') pills.push(<span key="skip" className="rec-pill">Skipped</span>)
  else if (e.status === 'paused' || e.bill.isPaused) pills.push(<span key="pause" className="rec-pill">Paused</span>)
  else if (!e.bill.isActive && e.status !== 'paid') pills.push(<span key="off" className="rec-pill">Inactive</span>)
  if (reconnect) pills.push(<span key="bank" className="rec-pill rec-pill--warn">Reconnect bank</span>)
  if (change) {
    pills.push(
      <span key="change" className={`rec-pill${change.direction === 'up' ? ' rec-pill--warn' : ''}`}>
        {change.direction === 'up' ? 'Increased' : 'Decreased'}{' '}
        <Money cents={change.deltaCents} currency={e.bill.currency} muted />
      </span>,
    )
  }
  return pills.length > 0 ? <span className="rec-row__chips">{pills}</span> : null
}

/**
 * Avatar, linked name and subline: the row anatomy the Passbook reuses.
 * `due`, when given, is stamped as `data-due` — the month board reads it to
 * light a hovered row's day on the grid. `DataGrid` offers a row class hook but
 * no row attributes, so a grid row's date rides on its lead cell; a card's
 * rides on the `<li>` itself. Opt-in, so the Passbook's rows carry none.
 */
export function Lead({ e, overdue, subline, due }: { e: Entry; overdue: boolean; subline: string; due?: string }) {
  return (
    <span className="rec-row__lead" data-due={due}>
      <MerchantAvatar merchant={identityOf(e)} size="row" />
      <span className="rec-row__text">
        <InlineLink to={recurringDetailPath(e.bill.id)} className="rec-row__name">
          {e.bill.name}
        </InlineLink>
        <span className={`rec-row__sub${overdue ? ' rec-row__sub--overdue' : ''}`}>
          {overdue && <Icon glyph={AlertTriangle} label="Overdue" size={12} />}
          {subline}
        </span>
      </span>
    </span>
  )
}

/** Expected, or what was actually paid (P5), always in the bill's own currency. */
export function Amount({ e, muted }: { e: Entry; muted: boolean }) {
  return (
    <span className="rec-row__amount">
      <Money
        cents={entryAmountCents(e)}
        currency={e.bill.currency}
        kind={e.bill.isInflow ? 'flow' : 'stock'}
        muted={muted}
      />
    </span>
  )
}

const rowKey = (e: Entry) => `${e.bill.id}:${e.dueDate}`

/**
 * One Money per currency, joined with "+", never summed (D21, Review Focus 3).
 * `slot` replaces the money entirely: the Passbook's Income band shows a count,
 * because inflows are in no total (D6).
 */
export function Totals({ totals, muted, slot }: { totals: CurrencyTotal[]; muted: boolean; slot?: ReactNode }) {
  if (slot !== undefined) return <span className="rec-section__total">{slot}</span>
  return (
    <span className="rec-section__total" data-testid="section-total">
      {totals.map((t, i) => (
        <Fragment key={t.currency}>
          {i > 0 && ' + '}
          <Money cents={t.cents} currency={t.currency} muted={muted} />
        </Fragment>
      ))}
    </span>
  )
}

/** A section's rows as `DataGrid` rows, or as a `<ul>` of cards (phone, and beside the desktop grid). */
export type RowLayout = 'grid' | 'cards'

/**
 * One section: a band (title and totals) over a `DataGrid`, or over a `<ul>` of
 * cards. The month list and the Passbook both render through it, so
 * a row's anatomy has one implementation. A caller supplies what differs: the
 * title, the row key (one per occurrence, or one per stream), each row's
 * subline, and `overdue` / `muted`, which style the band and the rows.
 */
export function SectionBlock({
  title,
  entries,
  totals,
  totalSlot,
  overdue,
  muted,
  rowKey,
  subline,
  layout,
  dated = false,
  changes,
  actions,
}: {
  title: string
  entries: Entry[]
  totals: CurrencyTotal[]
  totalSlot?: ReactNode
  overdue: boolean
  muted: boolean
  rowKey: (e: Entry) => string
  subline: (e: Entry) => string
  layout: RowLayout
  /** Stamp each row with its date (`data-due`), for the month board's row→day link. */
  dated?: boolean
  changes: AmountChanges
  actions: ((e: Entry) => ReactNode) | undefined
}) {
  const headingId = useId()
  // A card carries its date on the <li>; a grid row has no attribute hook, so its lead cell carries it.
  const lead = (e: Entry, due?: string) => <Lead e={e} overdue={overdue} subline={subline(e)} due={due} />
  const columns: Column<Entry>[] = [
    { key: 'payment', header: 'Payment', width: 'minmax(0, 1fr)', render: (e) => lead(e, dated ? e.dueDate : undefined) },
    { key: 'flags', header: 'Notes', width: 'auto', render: (e) => <Chips e={e} changes={changes} /> },
    // Ruling 3: Task 6 fills this slot with the row's actions. No placeholder.
    ...(actions ? [{ key: 'actions', header: 'Actions', width: 'auto', render: actions }] : []),
    { key: 'amount', header: 'Amount', width: 'minmax(96px, auto)', align: 'end', render: (e) => <Amount e={e} muted={muted} /> },
  ]
  return (
    <section aria-labelledby={headingId} className="rec-section">
      <div className={`rec-section__band${overdue ? ' rec-section__band--overdue' : ''}`}>
        {/* h2: the TopBar's h1 names the page, and the sections are its first level. */}
        <h2 id={headingId} className="rec-section__title">
          {title}
        </h2>
        <Totals totals={totals} muted={!overdue} slot={totalSlot} />
      </div>
      {layout === 'cards' ? (
        <ul className="rec-cards">
          {entries.map((e) => (
            <li key={rowKey(e)} className="rec-card" data-due={dated ? e.dueDate : undefined}>
              {lead(e)}
              <span className="rec-card__side">
                <Amount e={e} muted={muted} />
                <Chips e={e} changes={changes} />
                {actions?.(e)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rec-grid">
          <DataGrid
            columns={columns}
            groups={[{ label: '', rows: entries }]}
            rowKey={rowKey}
            label={`${title} payments`}
            empty={null}
            header="hidden"
          />
        </div>
      )}
    </section>
  )
}

/**
 * P2's sections in order — Overdue, Upcoming, Skipped, Paid in <Month> — each
 * absent when it has no rows (R4). `layout` picks `DataGrid` rows or cards
 * (the board chooses by breakpoint). Presentational: everything arrives as props.
 */
export function RecurringSections({
  sections,
  monthName,
  layout,
  today,
  accounts,
  changes,
  actions,
}: {
  sections: Record<SectionKey, Section>
  monthName: string
  layout: RowLayout
  today: LocalDate
  accounts: AccountNames
  changes: AmountChanges
  actions?: (e: Entry) => ReactNode
}) {
  return (
    <>
      {SECTION_ORDER.filter((k) => sections[k].entries.length > 0).map((k) => (
        <SectionBlock
          key={k}
          title={TITLES[k](monthName)}
          entries={sections[k].entries}
          totals={sections[k].totals}
          overdue={k === 'overdue'}
          muted={k === 'paid'}
          rowKey={rowKey}
          subline={(e) => sublineOf(e, k, today, accounts)}
          layout={layout}
          dated
          changes={changes}
          actions={actions}
        />
      ))}
    </>
  )
}
